// src/pages/Upload.tsx
import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { apiService } from '../services/api';
import '../styles/Upload.css';
import {  useNavigate, useLocation } from 'react-router-dom';
import dummyQr from '../assets/images/VASTAVIK_QR_Preview.png';
import vastavikBadge from '../assets/images/vastavik-qr-badge.png';
import { VALIDATION_FAILED_RECOVERY_MESSAGE } from '../constants/validationCopy';

import {
  moveFileApi,
  moveMultipleFilesApi,
} from '../services/api';
import BottomNav from '../components/BottomTabBar';

interface SelectedFile {
  id: string;
  file: File;
  preview: string;
  mediaType: 'image' | 'video' | 'audio' | 'other';
  dimensions?: { width: number; height: number };
}

interface ProtectionOptions {
  type: 'qr' | 'owner_key' | 'voiceover';
  qrPosition?: string;
  qrSize?: number;
  qrErrorCorrection?: string;
  ownerKeyPosition?: string;
  ownerKeyColor?: 'white' | 'black';
  ownerKeyFontSize?: number;
  voiceoverGender?: 'male' | 'female';
  nftType?: 'private' | 'public';
}

interface UploadResult {
  mediaId: string;
  filename: string;
  originalName: string;
  ownerKey: string;
  size: number;
  protectionType: string;
  creatorKey?: string;
  originalUrl?: string;
  mediaUrl?: string;
  mediaType: 'image' | 'video' | 'audio' | 'other';
  status?: string;
  error?: string;
  isAudio?: boolean;
  voiceoverPending?: boolean;
  voiceoverGender?: string;
  voiceoverUrl?: string;
}

const SUPPORTED_AUDIO_MIMES = [
  'audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/x-wav', 'audio/aac',
  'audio/mp4', 'audio/x-m4a', 'audio/m4a', 'audio/ogg', 'audio/flac',
  'audio/webm', 'audio/opus', 'audio/aiff', 'audio/x-aiff',
];

const SUPPORTED_AUDIO_EXTENSIONS = [
  'mp3', 'wav', 'aac', 'm4a', 'ogg', 'flac', 'webm', 'opus', 'aiff', 'aif'
];

const POSITIONS = [
  { id: 'top-left', label: 'Top Left', icon: '↖️' },
  { id: 'top-right', label: 'Top Right', icon: '↗️' },
  { id: 'bottom-left', label: 'Bottom Left', icon: '↙️' },
  { id: 'bottom-right', label: 'Bottom Right', icon: '↘️' },
  { id: 'center', label: 'Center', icon: '⊙' },
];

const ERROR_CORRECTIONS = [
  { id: 'L', label: 'Low' },
  { id: 'M', label: 'Medium' },
  { id: 'Q', label: 'Quartile' },
  { id: 'H', label: 'High' },
];

const generateOwnerKeyPreview = (creatorKey?: string): string => {
  const prefix = (creatorKey || 'VASTAVIK').toUpperCase();
  const randomDigit = Math.floor(Math.random() * 10).toString();
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let randomAlphanumeric = '';
  for (let i = 0; i < 7; i++) {
    randomAlphanumeric += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return `${prefix}${randomDigit}${randomAlphanumeric}`.substring(0, 16);
};

type VideoQuality = 'original' | 'high' | 'medium' | 'low';
// --- UPDATED to match old logic (factors and blurbs) ---
const VIDEO_QUALITY_OPTIONS: {
  value: VideoQuality; label: string; blurb: string; factor: number;
}[] = [
  { value: 'original', label: 'Original', blurb: 'Full quality & resolution', factor: 1.0 },
  { value: 'high',     label: 'High',     blurb: 'Near-original, smaller',    factor: 0.6 },
  { value: 'medium',   label: 'Medium',   blurb: 'Balanced · up to 1080p',    factor: 0.4 },
  { value: 'low',      label: 'Low',      blurb: 'Smallest · up to 720p',     factor: 0.22 },
];

// --- UPDATED to use the old formula ---
function estimateVideoSize(inputBytes: number, quality: VideoQuality): number {
  if (!inputBytes || inputBytes <= 0) return 0;
  if (quality === 'original') return inputBytes;
  const factor = VIDEO_QUALITY_OPTIONS.find(o => o.value === quality)?.factor ?? 1.0;
  return Math.round(inputBytes * 0.15 + inputBytes * 0.85 * factor);
}

function formatSize(bytes: number): string {
  if (!bytes || bytes <= 0) return '—';
  const mb = bytes / (1024 * 1024);
  return mb < 1 ? `${(bytes / 1024).toFixed(0)} KB` : mb < 1024 ? `${mb.toFixed(1)} MB` : `${(mb / 1024).toFixed(2)} GB`;
}

const TERMINAL_JOB_STATUSES = new Set([
  'COMPLETED', 'CONFIRMED', 'SUCCESS', 'REJECTED', 'FAILED',
]);

async function pollJobUntilDone(
  jobId: string,
  onTick?: (status: string, progress: number, message?: string) => void,
  opts: { intervalMs?: number; timeoutMs?: number } = {}
): Promise<import('../services/api').JobStatus | null> {
  const intervalMs = opts.intervalMs ?? 2500;
  const timeoutMs = opts.timeoutMs ?? 5 * 60 * 1000;
  const started = Date.now();
  let last: import('../services/api').JobStatus | null = null;
  while (Date.now() - started < timeoutMs) {
    try {
      const job = await apiService.getJobStatus(jobId);
      last = job;
      onTick?.(job.status, job.progress ?? 0, job.statusMessage);
      if (TERMINAL_JOB_STATUSES.has((job.status || '').toUpperCase())) return job;
    } catch (e) {
      // Transient poll error
    }
    await new Promise((r) => setTimeout(r, intervalMs));
  }
  return last;
}

const UploadDemo: React.FC = () => {
  const { user, logout } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const previewCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const modalCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const modalOverlayCanvasRef = useRef<HTMLCanvasElement | null>(null); // New ref for the playable video overlay
  const dummyQrRef = useRef<HTMLImageElement | null>(null);
  const badgeRef = useRef<HTMLImageElement | null>(null);

  useEffect(() => {
    const img = new Image();
    img.src = dummyQr;
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      dummyQrRef.current = img;
    };
    img.onerror = (e) => {
      console.error('Failed to load dummy QR image');
    };
    const badge = new Image();
    badge.src = vastavikBadge;
    badge.crossOrigin = 'anonymous';
    badge.onload = () => { badgeRef.current = badge; };
  }, []);

  const navigate = useNavigate();
  const location = useLocation();

  const goToLibrary = () => {
    try { window.dispatchEvent(new CustomEvent("vastavik:switch-tab", { detail: { tab: "library" } })); } catch { /* noop */ }
    navigate("/library");
  };

  const targetFolderId: string | null =
    (location.state as any)?.targetFolderId ?? null;

  const returnTo: string =
    (location.state as any)?.returnTo ?? 'dashboard';

  const [selectedFiles, setSelectedFiles] = useState<SelectedFile[]>([]);
  const [showProtectionSetup, setShowProtectionSetup] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('');
  const [uploadResults, setUploadResults] = useState<UploadResult[]>([]);
  const [error, setError] = useState('');

  const [protectionType, setProtectionType] = useState<'qr' | 'owner_key' | 'voiceover'>('qr');
  const [qrPosition, setQrPosition] = useState('bottom-right');
  const [qrSize, setQrSize] = useState(0);
  const [qrErrorCorrection, setQrErrorCorrection] = useState('M');

  const [ownerKeyPosition, setOwnerKeyPosition] = useState('bottom-right');
  const [ownerKeyColor, setOwnerKeyColor] = useState<'white' | 'black'>('white');
  const [ownerKeyFontSize, setOwnerKeyFontSize] = useState(0);

  const [isDragging, setIsDragging] = useState(false);

  const [previewOwnerKey, setPreviewOwnerKey] = useState<string>('');
  const [previewImage, setPreviewImage] = useState<HTMLImageElement | null>(null);
  const [calculatedQrSize, setCalculatedQrSize] = useState<number>(80);
  const [calculatedFontSize, setCalculatedFontSize] = useState<number>(16);
  const [showPreview, setShowPreview] = useState(false);

  const [previewVideo, setPreviewVideo] = useState<HTMLVideoElement | null>(null);
  const [videoPreviewReady, setVideoPreviewReady] = useState(false);

  const [redrawTrigger, setRedrawTrigger] = useState(0);
  const triggerRedraw = () => setRedrawTrigger(prev => prev + 1);

  const [activeFileId, setActiveFileId] = useState<string | null>(null);
  const [applyToAll, setApplyToAll] = useState<boolean>(true);
  const [perFileOptions, setPerFileOptions] = useState<Record<string, ProtectionOptions>>({});

  const [voiceoverGender, setVoiceoverGender] = useState<'male' | 'female'>('female');
  const [showAudioSetup, setShowAudioSetup] = useState(false);
  const [nftType, setNftType] = useState<'private' | 'public'>('private');
  const [videoQuality, setVideoQuality] = useState<'original' | 'high' | 'medium' | 'low'>('original');

  const hasAudioFiles = selectedFiles.some(f => f.mediaType === 'audio');

  const openEditorForFile = (fileId: string) => {
    setActiveFileId(fileId);
    setApplyToAll(false);
    setShowProtectionSetup(true);
    setTimeout(() => triggerRedraw(), 120);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (err) {
      console.error('Logout failed:', err);
    }
  };

  useEffect(() => {
    setPreviewOwnerKey(generateOwnerKeyPreview(user?.ownerId));
  }, [user?.ownerId]);

  const getMediaType = (file: File): 'image' | 'video' | 'audio' | 'other' => {
    if (file.type.startsWith('image/') ||
      file.name.toLowerCase().endsWith('.heic') ||
      file.name.toLowerCase().endsWith('.heif')) {
      return 'image';
    }
    
    if (file.type.startsWith('video/') ||
      ['mov', 'avi', 'mkv', 'webm', 'mp4'].includes(
        file.name.split('.').pop()?.toLowerCase() || ''
      )) {
      return 'video';
    }
    
    if (file.type.startsWith('audio/') || 
        SUPPORTED_AUDIO_MIMES.includes(file.type) ||
        SUPPORTED_AUDIO_EXTENSIONS.includes(
          file.name.split('.').pop()?.toLowerCase() || ''
        )) {
      return 'audio';
    }
    
    return 'other';
  };

  const handleFiles = (files: FileList | File[]) => {
    const fileArray = Array.from(files);
    const validFiles = fileArray.filter(file => {
      const ext = file.name.split('.').pop()?.toLowerCase() || '';
      
      const isValidImage = file.type.startsWith('image/') ||
        ['heic', 'heif'].includes(ext);
      const isValidVideo = file.type.startsWith('video/') ||
        ['mov', 'avi', 'mkv', 'webm', 'mp4'].includes(ext);
      const isValidAudio = file.type.startsWith('audio/') ||
        SUPPORTED_AUDIO_EXTENSIONS.includes(ext);
      
      const isValid = isValidImage || isValidVideo || isValidAudio;
      const isUnderLimit = file.size <= 100 * 1024 * 1024;
      return isValid && isUnderLimit;
    }).slice(0, 10);

    const newFiles: SelectedFile[] = validFiles.map(file => {
      const mediaType = getMediaType(file);
      let preview = '';
      if (mediaType === 'video') {
        preview = URL.createObjectURL(file);
      } else if (mediaType === 'image' && !['heic', 'heif'].includes(
        file.name.split('.').pop()?.toLowerCase() || ''
      )) {
        preview = URL.createObjectURL(file);
      }
      return {
        id: Math.random().toString(36).substr(2, 9),
        file,
        preview,
        mediaType,
      };
    });

    setSelectedFiles(prev => {
      const merged = [...prev, ...newFiles].slice(0, 10);
      return merged;
    });

    if (!activeFileId && newFiles.length > 0) {
      setActiveFileId(newFiles[0].id);
    }

    setError('');

    newFiles.forEach(file => {
      if (file.file.name.toLowerCase().endsWith('.heic') ||
        file.file.name.toLowerCase().endsWith('.heif')) {
        convertHEICForPreview(file);
      }
    });
  };

  const convertHEICForPreview = async (selectedFile: SelectedFile) => {
    try {
      if (typeof window !== 'undefined' && (window as any).heic2any) {
        const heic2any = (window as any).heic2any;
        const blob = new Blob([await selectedFile.file.arrayBuffer()], { type: selectedFile.file.type });
        const convertedBlob = await heic2any({
          blob: blob,
          toType: 'image/jpeg',
          quality: 0.8
        });

        const previewUrl = URL.createObjectURL(convertedBlob);

        setSelectedFiles(prev => prev.map(f =>
          f.id === selectedFile.id
            ? { ...f, preview: previewUrl, mediaType: 'image' }
            : f
        ));
      } else {
        const canvas = document.createElement('canvas');
        canvas.width = 400;
        canvas.height = 300;
        const ctx = canvas.getContext('2d');

        if (ctx) {
          ctx.fillStyle = '#f0f0f0';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.fillStyle = '#666';
          ctx.font = 'bold 48px Arial';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText('📷', canvas.width / 2, canvas.height / 2 - 40);
          ctx.font = '16px Arial';
          ctx.fillText('HEIC/HEIF Image', canvas.width / 2, canvas.height / 2 + 20);
          ctx.font = '14px Arial';
          ctx.fillText('Preview generated after upload', canvas.width / 2, canvas.height / 2 + 50);

          const previewUrl = canvas.toDataURL('image/jpeg');

          setSelectedFiles(prev => prev.map(f =>
            f.id === selectedFile.id
              ? { ...f, preview: previewUrl, dimensions: { width: 400, height: 300 } }
              : f
          ));
        }
      }
    } catch (err) {
      console.error('HEIC conversion error:', err);
      const errorPlaceholder = 'data:image/svg+xml;base64,' + btoa(`
            <svg width="300" height="200" xmlns="http://www.w3.org/2000/svg">
                <rect width="300" height="200" fill="#f8d7da"/>
                <text x="150" y="100" font-family="Arial" font-size="14" text-anchor="middle" fill="#721c24">
                    HEIC Preview Unavailable
                </text>
                <text x="150" y="120" font-family="Arial" font-size="12" text-anchor="middle" fill="#721c24">
                    Will be converted to JPEG on upload
                </text>
            </svg>
        `);

      setSelectedFiles(prev => prev.map(f =>
        f.id === selectedFile.id
          ? { ...f, preview: errorPlaceholder }
          : f
      ));
    }
  };

  useEffect(() => {
    const active = activeFileId
      ? selectedFiles.find(f => f.id === activeFileId)
      : selectedFiles.find(f => f.mediaType === 'image' || f.mediaType === 'video');

    if (!active) {
      setPreviewImage(null);
      setPreviewVideo(null);
      setVideoPreviewReady(false);

      const canvasesToClear = [previewCanvasRef.current, modalCanvasRef.current, modalOverlayCanvasRef.current];
      canvasesToClear.forEach(canvas => {
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      });

      return;
    }

    setVideoPreviewReady(false);
    setPreviewVideo(null);
    setPreviewImage(null);

    if (active.mediaType === 'video' && active.preview) {
      const video = document.createElement('video');
      video.src = active.preview;
      video.muted = true;
      video.playsInline = true;
      video.preload = 'auto';
      video.crossOrigin = 'anonymous';
      video.setAttribute('webkit-playsinline', 'true');
      video.setAttribute('playsinline', 'true');

      const onLoaded = async () => {
        const width = video.videoWidth || 640;
        const height = video.videoHeight || 360;

        try {
          const seekTime = Math.min(0.5, video.duration || 0.5);
          video.currentTime = seekTime;
          
          await new Promise<void>((resolve, reject) => {
            const timeout = setTimeout(() => {
              video.removeEventListener('seeked', onSeeked);
              reject(new Error('Seek timeout'));
            }, 5000);

            const onSeeked = () => {
              clearTimeout(timeout);
              video.removeEventListener('seeked', onSeeked);
              
              requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                  resolve();
                });
              });
            };
            
            video.addEventListener('seeked', onSeeked, { once: true });
          });
        } catch (e) { 
          try {
            await video.play();
            await new Promise(resolve => setTimeout(resolve, 100));
            video.pause();
            video.currentTime = Math.min(0.1, video.duration || 0.1);
            await new Promise(resolve => setTimeout(resolve, 50));
          } catch (playError) {
            console.error('❌ All thumbnail extraction methods failed:', playError);
          }
        }

        setPreviewVideo(video);
        setVideoPreviewReady(true);

        setSelectedFiles(prev => {
          let changed = false;
          const next = prev.map(f => {
            if (f.id !== active.id) return f;
            const prevDims = f.dimensions;
            if (prevDims && prevDims.width === width && prevDims.height === height) {
              return f;
            }
            changed = true;
            return { ...f, dimensions: { width, height } };
          });
          return changed ? next : prev;
        });

        setCalculatedQrSize(effectiveQrWidth(width, height, 0));
        setCalculatedFontSize(effectiveFontSize(width, height, 0));
        triggerRedraw();
      };

      const onError = (e: Event) => {
        console.error('❌ Video preview error:', e);
        setPreviewVideo(null);
        setVideoPreviewReady(false);
      };

      video.addEventListener('loadedmetadata', onLoaded);
      video.addEventListener('error', onError);
      video.load();

      return () => {
        video.removeEventListener('loadedmetadata', onLoaded);
        video.removeEventListener('error', onError);
      };
    }

    if (active.mediaType === 'image' && active.preview) {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        setPreviewImage(img);

        const width = img.naturalWidth;
        const height = img.naturalHeight;

        setSelectedFiles(prev => {
          let changed = false;
          const next = prev.map(f => {
            if (f.id !== active.id) return f;
            const prevDims = f.dimensions;
            if (prevDims && prevDims.width === width && prevDims.height === height) {
              return f;
            }
            changed = true;
            return { ...f, dimensions: { width, height } };
          });
          return changed ? next : prev;
        });

        setCalculatedQrSize(effectiveQrWidth(width, height, 0));
        setCalculatedFontSize(effectiveFontSize(width, height, 0));
        triggerRedraw();
      };
      img.onerror = (e) => {
        console.error('Image preview error:', e);
      };
      img.src = active.preview;
    }
  }, [selectedFiles, activeFileId]);

  const calcOptimalQRSize = (w: number, h: number, requested: number) => {
    const minDim = Math.min(w, h);
    const avg = (w + h) / 2;
    if (requested && requested > 0) {
      return Math.max(40, Math.min(requested, Math.floor(minDim * 0.25)));
    }
    let s = 100;
    if (avg < 800) s = 75;
    else if (avg < 1400) s = 100;
    else if (avg < 2200) s = 125;
    else if (avg < 3200) s = 150;
    else s = 175;
    return Math.max(40, Math.min(s, Math.floor(minDim * 0.14)));
  };

  const effectiveQrWidth = (w: number, h: number, requested: number) => {
    const size = calcOptimalQRSize(w, h, requested);
    return Math.min(Math.round(size), Math.floor(w * 0.95));
  };

  const effectiveFontSize = (w: number, h: number, requested: number) => {
    const minDim = Math.min(w, h);
    return requested && requested > 0 ? requested : Math.max(12, minDim * 0.02);
  };

  const drawQRPreview = (
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number,
    qrSize: number,
    qrPositionParam: string
  ) => {
    const qrImg = dummyQrRef.current;
    const aspect = qrImg && qrImg.naturalWidth ? qrImg.naturalHeight / qrImg.naturalWidth : 1.2;

    let badgeW = qrSize;
    let badgeH = badgeW * aspect;
    if (badgeH > height * 0.95) {
      const s = (height * 0.95) / badgeH;
      badgeW *= s;
      badgeH *= s;
    }

    const imagePadding = Math.max(10, Math.min(width, height) * 0.015);
    let x = 0, y = 0;
    switch (qrPositionParam) {
      case 'top-left':     x = imagePadding;                      y = imagePadding;                      break;
      case 'top-right':    x = width - badgeW - imagePadding;     y = imagePadding;                      break;
      case 'bottom-left':  x = imagePadding;                      y = height - badgeH - imagePadding;    break;
      case 'bottom-right': x = width - badgeW - imagePadding;     y = height - badgeH - imagePadding;    break;
      case 'center':       x = (width - badgeW) / 2;              y = (height - badgeH) / 2;             break;
      default:             x = width - badgeW - imagePadding;     y = height - badgeH - imagePadding;
    }

    if (qrImg) {
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(qrImg, x, y, badgeW, badgeH);
    }
  };

  const drawOwnerKeyPreview = (
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number,
    fontSize: number,
    ownerKeyPositionParam: string,
    ownerKeyColorParam: 'white' | 'black',
    keyText: string
  ) => {
    const padIn = fontSize * 0.5;
    const gap = fontSize * 0.35;
    const badgeH = fontSize * 1.5;
    const badgeW = badgeH;
    const avgCharWidth = fontSize * 0.62;
    const textWidth = Math.ceil(keyText.length * avgCharWidth);

    const pillW = Math.ceil(textWidth + gap + badgeW + padIn * 2);
    const pillH = Math.ceil(badgeH + padIn * 2);
    const cornerR = pillH * 0.22;
    const padding = Math.max(15, Math.min(width, height) * 0.02);

    let px = 0, py = 0;
    switch (ownerKeyPositionParam) {
      case 'top-left':     px = padding;                      py = padding; break;
      case 'top-right':    px = width - pillW - padding;      py = padding; break;
      case 'bottom-left':  px = padding;                      py = height - pillH - padding; break;
      case 'bottom-right': px = width - pillW - padding;      py = height - pillH - padding; break;
      case 'center':       px = (width - pillW) / 2;          py = (height - pillH) / 2; break;
      default:             px = width - pillW - padding;      py = height - pillH - padding;
    }
    px = Math.round(Math.max(padding, Math.min(px, width - pillW - padding)));
    py = Math.round(Math.max(padding, Math.min(py, height - pillH - padding)));

    const bgColor = ownerKeyColorParam === 'white' ? 'rgba(0,0,0,0.55)' : 'rgba(255,255,255,0.55)';
    const r = Math.min(cornerR, pillH / 2);
    ctx.beginPath();
    ctx.moveTo(px + r, py);
    ctx.arcTo(px + pillW, py, px + pillW, py + pillH, r);
    ctx.arcTo(px + pillW, py + pillH, px, py + pillH, r);
    ctx.arcTo(px, py + pillH, px, py, r);
    ctx.arcTo(px, py, px + pillW, py, r);
    ctx.closePath();
    ctx.fillStyle = bgColor;
    ctx.fill();

    ctx.fillStyle = ownerKeyColorParam === 'white' ? '#FFFFFF' : '#000000';
    ctx.font = `bold ${fontSize}px Arial`;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillText(keyText, px + padIn, py + pillH / 2);

    if (badgeRef.current) {
      const bx = px + padIn + textWidth + gap;
      const by = py + (pillH - badgeH) / 2;
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(badgeRef.current, bx, by, badgeW, badgeH);
    }
  };

  useEffect(() => {
    // Array mapping canvas to whether it should ONLY draw the watermark overlay (for playable video)
    const targets = [
      { canvas: previewCanvasRef.current, isOverlay: false },
      { canvas: modalCanvasRef.current, isOverlay: false },
      { canvas: modalOverlayCanvasRef.current, isOverlay: true }
    ].filter(t => t.canvas !== null) as { canvas: HTMLCanvasElement, isOverlay: boolean }[];
    
    if (targets.length === 0) return;

    const currentOpts: ProtectionOptions = applyToAll
      ? {
        type: protectionType,
        qrPosition,
        qrSize: qrSize || undefined,
        qrErrorCorrection,
        ownerKeyPosition,
        ownerKeyColor,
        ownerKeyFontSize: ownerKeyFontSize || undefined,
        nftType
      }
      : (activeFileId ? (perFileOptions[activeFileId] ?? {
        type: protectionType,
        qrPosition,
        qrSize: qrSize || undefined,
        qrErrorCorrection,
        ownerKeyPosition,
        ownerKeyColor,
        ownerKeyFontSize: ownerKeyFontSize || undefined,
        nftType
      }) : {
        type: protectionType,
        qrPosition,
        qrSize: qrSize || undefined,
        qrErrorCorrection,
        ownerKeyPosition,
        ownerKeyColor,
        ownerKeyFontSize: ownerKeyFontSize || undefined,
        nftType
      });

    let source: HTMLImageElement | HTMLVideoElement | null = null;
    let width = 0, height = 0;

    if (previewImage) {
      source = previewImage;
      width = previewImage.naturalWidth;
      height = previewImage.naturalHeight;
    } else if (previewVideo && videoPreviewReady) {
      source = previewVideo;
      width = previewVideo.videoWidth || 640;
      height = previewVideo.videoHeight || 360;
    }

    if (!source || width === 0 || height === 0) {
      return;
    }

    const drawableSource = source as CanvasImageSource;

    targets.forEach(({ canvas, isOverlay }) => {
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      try {
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        
        if (!isOverlay) {
          ctx.drawImage(drawableSource, 0, 0, width, height);
        }

        const finalQrSize = effectiveQrWidth(width, height, currentOpts.qrSize || 0);
        const finalFontSize = effectiveFontSize(width, height, currentOpts.ownerKeyFontSize || 0);

        if (currentOpts.type === 'qr') {
          drawQRPreview(ctx, width, height, finalQrSize, currentOpts.qrPosition || 'bottom-right');
        } else if (currentOpts.type === 'owner_key') {
          drawOwnerKeyPreview(ctx, width, height, finalFontSize, currentOpts.ownerKeyPosition || 'bottom-right', currentOpts.ownerKeyColor || 'white', previewOwnerKey);
        }
      } catch (err) {
        console.error('Canvas draw error:', err);
      }
    });
  }, [
    previewImage,
    previewVideo,
    videoPreviewReady,
    protectionType,
    qrPosition,
    qrSize,
    calculatedQrSize,
    ownerKeyPosition,
    ownerKeyColor,
    ownerKeyFontSize,
    calculatedFontSize,
    previewOwnerKey,
    redrawTrigger,
    showPreview,
    applyToAll,
    perFileOptions,
    activeFileId,
    qrErrorCorrection,
    nftType,
  ]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    handleFiles(e.dataTransfer.files);
  }, [activeFileId]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      handleFiles(e.target.files);
      e.currentTarget.value = '';
    }
  };

  const removeFile = (id: string) => {
    setSelectedFiles(prev => {
      const next = prev.filter(f => f.id !== id);
      if (id === activeFileId) {
        if (next.length > 0) setActiveFileId(next[0].id);
        else setActiveFileId(null);
      }
      return next;
    });
    setPerFileOptions(prev => {
      const copy = { ...prev };
      delete copy[id];
      return copy;
    });
  };

  const clearAll = () => {
    selectedFiles.forEach(f => {
      if (f.preview && f.preview.startsWith('blob:')) URL.revokeObjectURL(f.preview);
    });
    setSelectedFiles([]);
    setShowProtectionSetup(false);
    setShowAudioSetup(false);
    setPreviewImage(null);
    setPreviewVideo(null);
    setActiveFileId(null);
    setPerFileOptions({});
    const canvasesToClear = [previewCanvasRef.current, modalCanvasRef.current, modalOverlayCanvasRef.current];
    canvasesToClear.forEach(canvas => {
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    });
  };

  const hasImages = selectedFiles.some(f => f.mediaType === 'image');

  const handleProceed = () => {
    if (selectedFiles.length === 0) return;

    if (hasAudioFiles) {
      if (selectedFiles.every(f => f.mediaType === 'audio')) {
        setShowAudioSetup(true);
        if (!activeFileId && selectedFiles.length > 0) setActiveFileId(selectedFiles[0].id);
        return;
      }
      setError('Please upload audio files separately from images/videos');
      return;
    }

    if (hasImages || selectedFiles.some(f => f.mediaType === 'video')) {
      setShowProtectionSetup(true);
      if (!activeFileId && selectedFiles.length > 0) setActiveFileId(selectedFiles[0].id);
      setTimeout(() => {
        triggerRedraw();
      }, 300);
    } else {
      performUpload({
        type: 'qr',
        nftType
      });
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (!bytes || !Number.isFinite(bytes) || bytes <= 0) return '—';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.min(sizes.length - 1, Math.floor(Math.log(bytes) / Math.log(k)));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  useEffect(() => {
    if (!activeFileId) return;
    setPerFileOptions(prev => ({
      ...prev,
      [activeFileId]: {
        type: protectionType,
        qrPosition,
        qrSize: qrSize || undefined,
        qrErrorCorrection,
        ownerKeyPosition,
        ownerKeyColor,
        ownerKeyFontSize: ownerKeyFontSize || undefined,
        nftType
      }
    }));
  }, [activeFileId, protectionType, qrPosition, qrSize, qrErrorCorrection, ownerKeyPosition, ownerKeyColor, ownerKeyFontSize, nftType]);

  useEffect(() => {
    if (!activeFileId || applyToAll) return;
    const opts = perFileOptions[activeFileId];
    if (!opts) return;

    setProtectionType(opts.type);
    if (opts.type === 'qr') {
      setQrPosition(opts.qrPosition || 'bottom-right');
      setQrSize(opts.qrSize ?? 0);
      setQrErrorCorrection(opts.qrErrorCorrection || 'M');
    } else if (opts.type === 'owner_key') {
      setOwnerKeyPosition(opts.ownerKeyPosition || 'bottom-right');
      setOwnerKeyColor(opts.ownerKeyColor || 'white');
      setOwnerKeyFontSize(opts.ownerKeyFontSize ?? 0);
    }
    setNftType(opts.nftType || 'private');
  }, [activeFileId, applyToAll]);

  const performUpload = async (options: ProtectionOptions) => {
    setIsUploading(true);
    setUploadProgress(0);
    setUploadStatus("Preparing upload...");
    setError("");
    setShowProtectionSetup(false);
    setShowAudioSetup(false);

    try {
      const results: UploadResult[] = [];
      const totalFiles = selectedFiles.length;

      for (let i = 0; i < selectedFiles.length; i++) {
        const selectedFile = selectedFiles[i];

        setUploadStatus(
          `Uploading ${selectedFile.file.name} (${i + 1}/${totalFiles})...`
        );

        const fileOptions: ProtectionOptions = applyToAll
          ? options
          : (perFileOptions[selectedFile.id] ?? options);

        try {
          const formData = new FormData();
          formData.append("files", selectedFile.file);
          formData.append("nftType", fileOptions.nftType || 'private');

          if (selectedFile.mediaType === 'audio') {
            formData.append('protectionType', 'voiceover');
            formData.append('voiceoverGender', options.voiceoverGender || 'female');
          } else if (
            selectedFile.mediaType === "image" ||
            selectedFile.mediaType === "video"
          ) {
            formData.append("protectionType", fileOptions.type);

            if (selectedFile.mediaType === "video") {
              formData.append("videoQuality", videoQuality);
            }

            if (fileOptions.type === "qr") {
              formData.append("qrPosition", fileOptions.qrPosition || "bottom-right");
              if (fileOptions.qrSize) {
                formData.append("qrSize", String(fileOptions.qrSize));
              }
              formData.append(
                "qrErrorCorrection",
                fileOptions.qrErrorCorrection || "M"
              );
            } else {
              formData.append(
                "ownerKeyPosition",
                fileOptions.ownerKeyPosition || "bottom-right"
              );
              formData.append(
                "ownerKeyColor",
                fileOptions.ownerKeyColor || "white"
              );
              if (fileOptions.ownerKeyFontSize) {
                formData.append(
                  "ownerKeyFontSize",
                  String(fileOptions.ownerKeyFontSize)
                );
              }
            }
          }

          const response = await apiService.uploadMediaWithProtection(
            formData,
            (progress) => {
              const frac = (Math.min(Math.max(progress, 0), 100) / 100) * 0.5;
              setUploadProgress(((i + frac) / totalFiles) * 100);
            }
          );

          const uploadedFile = response?.data?.uploadedFiles?.[0];

          if (uploadedFile) {
            const jobId = uploadedFile.jobId ?? uploadedFile.job_id;
            let job: import('../services/api').JobStatus | null = null;
            const alreadyDone = ['completed', 'confirmed'].includes(
              String(uploadedFile.status || '').toLowerCase()
            );
            if (jobId && !alreadyDone) {
              setUploadStatus(
                `Validating ${selectedFile.file.name} (${i + 1}/${totalFiles})…`
              );
              job = await pollJobUntilDone(jobId, (status, progress, message) => {
                const frac = 0.5 + Math.min(Math.max(progress, 0), 100) / 200;
                setUploadProgress(((i + frac) / totalFiles) * 100);
                if (message) {
                  setUploadStatus(`${message} (${i + 1}/${totalFiles})`);
                }
              });
            }

            const finalStatus = (job?.status || uploadedFile.status || 'processing').toUpperCase();
            const isRejected = job?.isRejected === true || finalStatus === 'REJECTED';
            const isDone = ['COMPLETED', 'CONFIRMED', 'SUCCESS'].includes(finalStatus);

            if (isRejected) {
              results.push({
                mediaId: job?.mediaId || uploadedFile.mediaId || '',
                filename: uploadedFile.filename,
                originalName: uploadedFile.originalName,
                ownerKey: '',
                size: uploadedFile.size,
                protectionType: uploadedFile.protectionType || fileOptions.type,
                mediaType: selectedFile.mediaType,
                status: 'failed',
                error: job?.rejectionReason || 'This media could not be validated.',
              });
            } else {
              results.push({
                mediaId: job?.mediaId || uploadedFile.mediaId,
                filename: uploadedFile.filename,
                originalName: uploadedFile.originalName,
                ownerKey: job?.ownerKey || uploadedFile.ownerKey,
                creatorKey:
                  uploadedFile.creatorKey ?? job?.ownerKey ?? uploadedFile.ownerKey ?? uploadedFile.owner_key,
                size: uploadedFile.size,
                protectionType: uploadedFile.protectionType || fileOptions.type,
                mediaUrl: uploadedFile.mediaUrl,
                mediaType: selectedFile.mediaType,
                status: isDone ? 'completed' : 'processing',
                isAudio: selectedFile.mediaType === 'audio',
                voiceoverPending: uploadedFile.voiceoverPending,
                voiceoverGender: uploadedFile.voiceoverGender,
              });
            }
          }
        } catch (fileError: any) {
          console.error(`Upload error for ${selectedFile.file.name}:`, fileError);

          results.push({
            mediaId: "",
            filename: selectedFile.file.name,
            originalName: selectedFile.file.name,
            ownerKey: "",
            size: selectedFile.file.size,
            protectionType: fileOptions.type,
            mediaType: selectedFile.mediaType,
            status: "failed",
            error: fileError?.message || "Upload failed",
          });
        }
      }

      setUploadResults(results);
      setUploadProgress(100);
      const okCount = results.filter((r) => r.status === 'completed').length;
      const failCount = results.filter((r) => r.status === 'failed').length;
      const pendingCount = results.filter((r) => r.status === 'processing').length;
      setUploadStatus(
        failCount === results.length
          ? 'Upload failed'
          : failCount > 0
            ? `${okCount} registered, ${failCount} failed`
            : pendingCount > 0
              ? 'Uploaded — still processing…'
              : 'Upload complete!'
      );

      try {
        localStorage.setItem("vastavik:lastUpload", String(Date.now()));
        window.dispatchEvent(new Event("vastavik:upload-completed"));
      } catch { /* ignore */ }

      selectedFiles.forEach((f) => {
        if (f.preview && f.preview.startsWith('blob:')) URL.revokeObjectURL(f.preview);
      });

      setSelectedFiles([]);
      setPreviewImage(null);
      setPreviewVideo(null);
      setActiveFileId(null);
      setPerFileOptions({});

      if (targetFolderId) {
        const creatorKeys = results
          .filter((r) => r.status === "completed")
          .map((r) => r.creatorKey)
          .filter((k): k is string => typeof k === "string" && k.length > 0);

        try {
          if (creatorKeys.length === 1) {
            await moveFileApi(creatorKeys[0], targetFolderId);
          } else if (creatorKeys.length > 1) {
            await moveMultipleFilesApi(creatorKeys, targetFolderId);
          }
        } catch (err) {
          console.error('Failed to move files to folder:', err);
        }
      }

      if (returnTo === "library" && targetFolderId) {
        navigate("/library", {
          state: {
            openFolderId: targetFolderId,
          },
          replace: true,
        });
      }
    } catch (err: any) {
      console.error('Upload error:', err);
      setError(err?.message || "Upload failed");
    } finally {
      setIsUploading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const resetUpload = () => {
    setUploadResults([]);
    setSelectedFiles([]);
    setUploadProgress(0);
    setUploadStatus('');
    setError('');
    setProtectionType('qr');
    setPreviewImage(null);
    setPreviewVideo(null);
    setActiveFileId(null);
    setPerFileOptions({});
    setNftType('private');
  };

  // --- REWRITTEN AUDIO SETUP VIEW (keeps new UI) ---
  const AudioSetupView = () => {
    const currentIndex = selectedFiles.findIndex(f => f.id === activeFileId) > -1 ? selectedFiles.findIndex(f => f.id === activeFileId) : 0;
    const activeFile = selectedFiles[currentIndex] || selectedFiles[0];

    return (
      <div className="w-full max-w-6xl mx-auto flex flex-col animate-in fade-in zoom-in duration-300 pb-10 text-black dark:text-white font-['Product_Sans']">
        {/* Header & Back Button */}
        <div className="mb-8 mt-4">
          <button 
            onClick={() => setShowAudioSetup(false)} 
            className="flex items-center gap-2 px-4 py-2 border border-black dark:border-[#2c2c2c] rounded-lg bg-transparent hover:bg-gray-100 dark:hover:bg-[#222] transition-colors mb-8 text-sm text-gray-700 dark:text-gray-300"
          >
            ← Back to Selection
          </button>
          <h1 className="text-[32px] font-bold mb-1 text-black dark:text-white">Configure Protection</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400">Set up watermark and NFT registration details.</p>
        </div>

        {/* Main Two-Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-[446px_1fr] gap-6 items-start">
          
          {/* LEFT COLUMN */}
          <div className="flex flex-col gap-6">
            
            {/* Selected File Navigator */}
            <div className="w-full p-6 bg-white dark:bg-[#121212] border border-black dark:border-[#2c2c2c] rounded-lg shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-[11px] uppercase tracking-widest text-gray-600 dark:text-gray-400 font-semibold">
                  Selected File ({currentIndex + 1} of {selectedFiles.filter(f => f.mediaType === 'audio').length})
                </h3>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      const audioFiles = selectedFiles.filter(f => f.mediaType === 'audio');
                      const cIdx = audioFiles.findIndex(f => f.id === activeFileId);
                      if (cIdx > 0) setActiveFileId(audioFiles[cIdx - 1].id);
                    }}
                    disabled={currentIndex <= 0}
                    className="w-8 h-8 flex items-center justify-center border border-black dark:border-[#2c2c2c] text-gray-600 dark:text-gray-400 bg-transparent hover:bg-gray-100 dark:hover:bg-[#222] disabled:opacity-30 disabled:hover:bg-transparent rounded transition-colors cursor-pointer"
                  >
                    ‹
                  </button>
                  <button
                    onClick={() => {
                      const audioFiles = selectedFiles.filter(f => f.mediaType === 'audio');
                      const cIdx = audioFiles.findIndex(f => f.id === activeFileId);
                      if (cIdx < audioFiles.length - 1) setActiveFileId(audioFiles[cIdx + 1].id);
                    }}
                    disabled={currentIndex >= selectedFiles.filter(f => f.mediaType === 'audio').length - 1}
                    className="w-8 h-8 flex items-center justify-center border border-black dark:border-[#2c2c2c] text-gray-600 dark:text-gray-400 bg-transparent hover:bg-gray-100 dark:hover:bg-[#222] disabled:opacity-30 disabled:hover:bg-transparent rounded transition-colors cursor-pointer"
                  >
                    ›
                  </button>
                </div>
              </div>
              <div className="w-full p-3 bg-gray-50 dark:bg-[#0a0a0a] border border-black dark:border-[#2c2c2c] rounded flex items-center gap-3 text-sm">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-black dark:text-white opacity-70">
                  <path d="M9 18V5l12-2v13"></path>
                  <circle cx="6" cy="18" r="3"></circle>
                  <circle cx="18" cy="16" r="3"></circle>
                </svg>
                <span className="truncate text-gray-800 dark:text-gray-200">{activeFile?.file.name}</span>
              </div>
            </div>

            {/* How it works */}
            <div className="w-full p-6 bg-white dark:bg-[#121212] border border-black dark:border-[#2c2c2c] rounded-lg shadow-sm">
              <h3 className="text-[16px] font-bold text-black dark:text-white mb-4">How it works</h3>
              <ul className="space-y-4 text-[13px] text-gray-700 dark:text-gray-300">
                <li className="flex gap-3 items-start">
                  <span className="text-gray-500 dark:text-gray-500 font-mono mt-0.5">01</span>
                  <span>Audio is registered on the blockchain for permanent proof.</span>
                </li>
                <li className="flex gap-3 items-start">
                  <span className="text-gray-500 dark:text-gray-500 font-mono mt-0.5">02</span>
                  <span>We generate a Vastavik Voice ID—a unique audio watermark.</span>
                </li>
                <li className="flex gap-3 items-start">
                  <span className="text-gray-500 dark:text-gray-500 font-mono mt-0.5">03</span>
                  <span>Share the Voice ID alongside your track to verify authenticity.</span>
                </li>
              </ul>
            </div>

            {/* NFT Registration Type */}
            <div className="w-full p-6 bg-white dark:bg-[#121212] border border-black dark:border-[#2c2c2c] rounded-lg shadow-sm">
              <h3 className="text-[16px] font-bold text-black dark:text-white mb-4">NFT Registration</h3>
              <div className="space-y-3">
                <button
                  onClick={() => setNftType('private')}
                  className={`w-full flex items-start gap-4 p-4 rounded-lg border transition-all duration-300 text-left ${
                    nftType === 'private'
                      ? 'border-black dark:border-white bg-gray-100 dark:bg-[#1a1a1a]'
                      : 'border-black dark:border-[#2c2c2c] bg-transparent hover:border-gray-500 dark:hover:border-[#333]'
                  }`}
                >
                  <div className={`mt-0.5 w-4 h-4 rounded-full border flex items-center justify-center ${nftType === 'private' ? 'border-black dark:border-white' : 'border-gray-400 dark:border-[#555]'}`}>
                    {nftType === 'private' && <div className="w-2 h-2 rounded-full bg-black dark:bg-white"></div>}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-bold text-black dark:text-white text-[14px]">Private NFT</span>
                      <span className="bg-gray-200 dark:bg-[#2c2c2c] text-gray-700 dark:text-gray-300 text-[10px] px-2 py-0.5 rounded-sm">Default</span>
                    </div>
                    <span className="block text-xs text-gray-600 dark:text-gray-500">Only you and approved users can view.</span>
                  </div>
                </button>

                <button
                  disabled
                  className="w-full flex items-start gap-4 p-4 rounded-lg border border-black dark:border-[#2c2c2c] bg-transparent opacity-60 cursor-not-allowed text-left"
                >
                  <div className="mt-0.5 w-4 h-4 rounded-full border border-gray-400 dark:border-[#555] flex items-center justify-center"></div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-bold text-gray-700 dark:text-gray-300 text-[14px]">Public NFT</span>
                      <span className="bg-gray-200 dark:bg-[#2c2c2c] text-gray-600 dark:text-gray-400 text-[10px] px-2 py-0.5 rounded-sm">Coming Soon</span>
                    </div>
                    <span className="block text-xs text-gray-600 dark:text-gray-500">Visible to everyone on the blockchain.</span>
                  </div>
                </button>
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN */}
          <div className="flex flex-col gap-6 w-full min-w-0">
            
            {/* Selected Tracks List */}
            <div className="w-full bg-white dark:bg-[#121212] border border-black dark:border-[#2c2c2c] rounded-lg p-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-[13px] font-bold text-black dark:text-white">Selected Tracks</h3>
                <span className="bg-gray-200 dark:bg-[#222] text-xs text-gray-700 dark:text-gray-300 px-2.5 py-0.5 rounded-md border border-gray-300 dark:border-[#333]">
                  {selectedFiles.filter(f => f.mediaType === 'audio').length}
                </span>
              </div>
              <div className="space-y-3 max-h-60 overflow-y-auto custom-scrollbar">
                {selectedFiles.filter(f => f.mediaType === 'audio').map(file => (
                  <div key={file.id} className="flex flex-col sm:flex-row sm:items-center gap-4 p-4 bg-gray-50 dark:bg-[#1a1a1a] rounded-lg border border-black dark:border-[#2c2c2c]">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className="w-10 h-10 bg-gray-200 dark:bg-[#222] rounded flex items-center justify-center text-gray-600 dark:text-gray-400">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M9 18V5l12-2v13"></path>
                          <circle cx="6" cy="18" r="3"></circle>
                          <circle cx="18" cy="16" r="3"></circle>
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[13px] font-medium text-black dark:text-white truncate">{file.file.name}</p>
                        <p className="text-[11px] text-gray-600 dark:text-gray-500 mt-0.5">{formatFileSize(file.file.size)}</p>
                      </div>
                    </div>
                    <button 
                      className="px-4 py-2 bg-black text-white dark:bg-white dark:text-black text-[12px] font-bold rounded hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors shrink-0"
                      onClick={() => performUpload({ type: 'voiceover', voiceoverGender, nftType })}
                    >
                      Process Audio
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Select Protection Voice */}
            <div className="w-full bg-transparent">
              <h3 className="text-[13px] font-bold text-black dark:text-white mb-4">Select Protection Voice</h3>
              <div className="space-y-3">
                <button
                  className={`w-full flex items-center gap-4 p-5 rounded-lg border transition-all duration-300 text-left ${
                    voiceoverGender === 'female' 
                    ? 'border-black dark:border-white bg-gray-100 dark:bg-[#1a1a1a]' 
                    : 'border-black dark:border-[#2c2c2c] bg-white dark:bg-[#121212] hover:border-gray-500'
                  }`}
                  onClick={() => setVoiceoverGender('female')}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center border transition-colors ${voiceoverGender === 'female' ? 'border-black dark:border-white text-black dark:text-white' : 'border-gray-400 dark:border-gray-600 text-gray-600 dark:text-gray-400'}`}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                      <circle cx="12" cy="7" r="4"></circle>
                    </svg>
                  </div>
                  <div className="flex-1">
                    <span className="block font-bold text-black dark:text-white text-[14px]">Female Voice</span>
                    <span className="block text-[12px] text-gray-600 dark:text-gray-500 mt-0.5">British accent | Natural tone</span>
                  </div>
                </button>
                
                <button
                  className={`w-full flex items-center gap-4 p-5 rounded-lg border transition-all duration-300 text-left ${
                    voiceoverGender === 'male' 
                    ? 'border-black dark:border-white bg-gray-100 dark:bg-[#1a1a1a]' 
                    : 'border-black dark:border-[#2c2c2c] bg-white dark:bg-[#121212] hover:border-gray-500'
                  }`}
                  onClick={() => setVoiceoverGender('male')}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center border transition-colors ${voiceoverGender === 'male' ? 'border-black dark:border-white text-black dark:text-white' : 'border-gray-400 dark:border-gray-600 text-gray-600 dark:text-gray-400'}`}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                      <circle cx="12" cy="7" r="4"></circle>
                    </svg>
                  </div>
                  <div className="flex-1">
                    <span className="block font-bold text-black dark:text-white text-[14px]">Male Voice</span>
                    <span className="block text-[12px] text-gray-600 dark:text-gray-500 mt-0.5">British accent | Deep tone</span>
                  </div>
                </button>
              </div>
            </div>

            {/* Upload Button */}
            <div className="w-full flex justify-end mt-4">
              <button 
                onClick={() => performUpload({ type: 'voiceover', voiceoverGender, nftType })} 
                className="px-8 py-3 rounded-md bg-black text-white dark:bg-white dark:text-black font-bold text-sm shadow-lg hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors"
              >
                Upload & Protect
              </button>
            </div>
            
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="w-full h-full text-black dark:text-white bg-transparent border-none overflow-y-auto pb-10 transition-colors duration-300">
      <div className="w-full max-w-[1080px] mx-auto px-4 sm:px-6 pt-8 pb-8 flex flex-col">
        
{/* Upload Progress View */}
{isUploading && (() => {
  const p = Math.max(0, Math.min(100, uploadProgress));
  const C = 2 * Math.PI * 70;
  const phases = [
    { text: "Uploading files", active: p > 1 && p < 85, done: p >= 85 },
    { text: "Verifying & protecting", active: p >= 85 && p < 100, done: p >= 100 },
    { text: "Blockchain registration", active: p >= 100, done: false },
  ];

  return (
    <div className="flex flex-col items-center justify-center w-full py-12 px-4">
      
      {/* 1. LARGE LOADER: Gradient removed, uses currentColor with dark:text-white */}
      <div className="relative flex items-center justify-center w-[180px] h-[180px] mb-8">
        <svg className="w-full h-full -rotate-90" viewBox="0 0 160 160">
          {/* Background Track */}
          <circle 
            cx="80" cy="80" r="70" fill="none" stroke="currentColor" 
            className="text-gray-200 dark:text-[#2C2C2C]" 
            strokeWidth="8" 
          />
          {/* Active Progress */}
          <circle
            cx="80" cy="80" r="70" fill="none" stroke="currentColor" 
            className="text-black dark:text-white" 
            strokeWidth="8" strokeLinecap="round"
            strokeDasharray={C}
            strokeDashoffset={C * (1 - Math.max(0.01, p / 100))}
            style={{ transition: "stroke-dashoffset 0.3s ease" }}
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-[48px] leading-none font-bold font-['Product_Sans'] text-black dark:text-white tracking-tight">
            {Math.round(p)}%
          </span>
        </div>
      </div>

      <h2 className="text-[22px] md:text-[28px] font-bold font-['Product_Sans'] text-black dark:text-white text-center tracking-tight mb-4 max-w-md line-clamp-2 px-2">
        {uploadStatus || "Uploading…"}
      </h2>

      <div className="flex items-center gap-2 border border-[#EF4444] rounded-full px-5 py-1.5 bg-white dark:bg-transparent mb-10">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#EF4444" strokeWidth="2">
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
          <line x1="12" y1="9" x2="12" y2="13" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
        <span className="text-[#EF4444] text-[13px] md:text-[14px] font-normal font-['Product_Sans']">
          Keep this tab open during upload
        </span>
      </div>

      {/* 2. PHASES BOX */}
      <div className="w-full max-w-[480px] rounded-md border border-[#E5E7EB] dark:border-[#2C2C2C] bg-white dark:bg-[#121212] shadow-sm dark:shadow-none flex flex-col">
        {phases.map((ph, index) => (
          <div
            key={ph.text}
            className={`relative flex items-center gap-4 px-8 py-5 ${index !== phases.length - 1 ? 'border-b border-[#F3F4F6] dark:border-[#222222]' : ''}`}
          >
            {/* Active Indicator aligned to the left edge */}
            {ph.active && (
              <div className="absolute left-[-1px] top-1/2 -translate-y-1/2 w-[4px] h-[24px] bg-black dark:bg-white rounded-r-md"></div>
            )}
            
            <div className="flex-shrink-0">
              {ph.done ? (
                <div className="w-[22px] h-[22px] rounded-full bg-black dark:bg-white flex items-center justify-center">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" className="text-white dark:text-black" strokeWidth="3">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                </div>
              ) : ph.active ? (
                /* SMALL LOADER: Updated to be clearly visible (White in dark mode, Black in light) */
                <div className="relative w-[22px] h-[22px] flex items-center justify-center">
                  <div className="absolute inset-0 rounded-full border-[1.5px] border-gray-200 dark:border-[#333]"></div>
                  <div className="absolute inset-0 rounded-full border-[1.5px] border-black border-t-transparent dark:border-white dark:border-t-transparent animate-spin"></div>
                </div>
              ) : (
                <div className="w-[22px] h-[22px] rounded-full border-[1.5px] border-[#D1D5DB] dark:border-[#555]"></div>
              )}
            </div>
            
            <span className={`text-[15px] font-['Product_Sans'] ${ph.done || ph.active ? "text-black dark:text-white" : "text-[#9CA3AF] dark:text-gray-500"}`}>
              {ph.text}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
})()}

{/* Upload Results View */}
        {!isUploading && uploadResults.length > 0 && (() => {
          const successItems = uploadResults.filter(r => !r.error || Boolean(r.error && r.mediaType === 'video' && (r.error.includes('504') || r.error.toLowerCase().includes('gateway timeout') || r.error === 'Network Error')));
          const rejectedItems = uploadResults.filter(r => r.error && !successItems.includes(r) && r.error.toLowerCase().includes('already uploaded'));
          const failedItems = uploadResults.filter(r => r.error && !successItems.includes(r) && !rejectedItems.includes(r));
          const allSuccess = successItems.length === uploadResults.length;

          return (
            <div className="flex flex-col items-center w-full bg-transparent font-['Product_Sans'] py-12 px-4 animate-in fade-in zoom-in duration-300">
              {/* Header */}
              <div className="flex flex-col items-center mb-10 text-center">
                {allSuccess ? (
                  <>
                    <svg width="46" height="46" viewBox="0 0 24 24" fill="none" stroke="currentColor" className="text-black dark:text-white mb-4" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                      <polyline points="9 12 11 14 15 10" />
                    </svg>
                    <h1 className="font-['Caviar_Dreams'] font-bold text-[36px] md:text-[40px] leading-[1.2] text-[#1B1B1B] dark:text-white mb-2">
                      Upload Received
                    </h1>
                    <p className="text-[#666666] dark:text-gray-400 text-[15px] md:text-[16px] max-w-[380px] leading-relaxed">
                      Your media is being validated, and registered on the blockchain in the background.
                    </p>
                  </>
                ) : (
                  <>
                    <div className="flex items-center justify-center w-12 h-12 rounded-full bg-[#FEF2F2] dark:bg-[#2C1310] mb-4">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" className="text-[#EF4444] dark:text-[#F97316]" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" y1="8" x2="12" y2="12" />
                        <line x1="12" y1="16" x2="12.01" y2="16" />
                      </svg>
                    </div>
                    <h1 className="font-['Caviar_Dreams'] font-bold text-[36px] md:text-[40px] leading-[1.2] text-[#1B1B1B] dark:text-white mb-2">
                      Upload Issues
                    </h1>
                    <p className="text-[#666666] dark:text-gray-400 text-[15px] md:text-[16px]">
                      Some files could not be uploaded.
                    </p>
                  </>
                )}
              </div>

              {/* Info Pill (only when everything succeeded) */}
              {allSuccess && (
                <div className="inline-flex items-center justify-center gap-2 border border-gray-300 dark:border-[#444] bg-white dark:bg-transparent rounded-full px-5 py-2.5 mb-10 shadow-sm">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" className="text-black dark:text-white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                    <line x1="9" y1="3" x2="9" y2="21"></line>
                  </svg>
                  <span className="text-[14px] text-gray-800 dark:text-gray-300">
                    Track progress on each item in your <strong className="text-black dark:text-white font-medium">Library</strong>.
                  </span>
                </div>
              )}

              <div className="w-full max-w-[768px]">

                {/* Rejected Section */}
                {rejectedItems.length > 0 && (
                  <div className="mb-8">
                    <div className="flex items-center justify-between pb-3 border-b border-[#EAEAEA] dark:border-[#2A2A2A] mb-5">
                      <div className="flex items-center gap-2.5">
                        <div className="flex items-center justify-center w-[22px] h-[22px] rounded-full bg-[#DC2626]">
                          <svg width="10" height="10" viewBox="0 0 14 14" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M1 1L13 13M13 1L1 13" />
                          </svg>
                        </div>
                        <h2 className="font-bold text-[20px] text-[#1B1B1B] dark:text-white leading-none">Rejected</h2>
                      </div>
                      <span className="text-[12px] font-medium text-[#999999] uppercase tracking-wider">
                        {rejectedItems.length} FILE{rejectedItems.length > 1 ? 'S' : ''}
                      </span>
                    </div>

                    <div className="space-y-4">
                      {rejectedItems.map((r, i) => (
                        <div key={`rej-${i}`} className="w-full rounded-[12px] border border-[#CFC4C5] dark:border-[#333333] bg-white dark:bg-[#121212] p-[24px] md:p-[28px]">
                          <div className="flex items-center gap-4">
                            <div className="flex items-center justify-center w-[48px] h-[48px] shrink-0 rounded-lg bg-[#F5F5F5] dark:bg-[#1E1E1E] text-[#666666] dark:text-gray-400">
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M14 2H6C4.89543 2 4 2.89543 4 4V20C4 21.1046 4.89543 22 6 22H18C19.1046 22 20 21.1046 20 20V8L14 2Z" />
                              </svg>
                            </div>
                            <div className="flex flex-col min-w-0">
                              <p className="font-medium text-[16px] md:text-[18px] text-[#1B1B1B] dark:text-white truncate">
                                {r.originalName}
                              </p>
                              <p className="text-[11px] md:text-[12px] font-medium text-[#999999] uppercase mt-0.5 tracking-wide">
                                Duplicate Media
                              </p>
                            </div>
                          </div>

                          <div className="w-full h-[1px] bg-[#EAEAEA] dark:bg-[#2A2A2A] my-[18px]"></div>

                          <p className="font-medium text-[14px] md:text-[15px] text-[#D32F2F] dark:text-[#EF4444] leading-relaxed">
                            You've already uploaded this media to Vastavik, so it wasn't processed again.
                          </p>

                          <div className="mt-5 bg-[#F8F8F8] dark:bg-[#1A1A1A] border-l-[4px] border-[#999999] dark:border-[#555555] rounded-r-md py-3 px-4">
                            <p className="text-[#666666] dark:text-gray-300 text-[13px] font-normal">
                              Think this is an error? Email{" "}
                              <a href="mailto:support@vastavik.org" className="underline underline-offset-2 font-medium text-[#1B1B1B] dark:text-white hover:text-black dark:hover:text-gray-300">
                                support@vastavik.org
                              </a>{" "}
                              with the media and these details.
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Failed Section */}
                {failedItems.length > 0 && (
                  <div className="mb-8">
                    <div className="flex items-center justify-between pb-3 border-b border-[#EAEAEA] dark:border-[#2A2A2A] mb-5">
                      <div className="flex items-center gap-2 text-[#EF4444] dark:text-[#F97316]">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="12" r="10" />
                          <line x1="12" y1="8" x2="12" y2="12" />
                          <line x1="12" y1="16" x2="12.01" y2="16" />
                        </svg>
                        <h2 className="font-medium text-[16px] md:text-[18px] leading-none">Failed</h2>
                      </div>
                      <span className="flex items-center justify-center w-[22px] h-[22px] rounded-full bg-[#F3F4F6] dark:bg-[#3E1C10] text-[#6B7280] dark:text-[#F97316] text-[11px] font-bold">
                        {failedItems.length}
                      </span>
                    </div>

                    <div className="space-y-4">
                      {failedItems.map((r, i) => (
                        <div key={`fail-${i}`} className="w-full rounded-[6px] border border-gray-200 dark:border-[#333] bg-white dark:bg-[#121212] p-[16px] md:p-[20px] flex sm:flex-row flex-col sm:items-center gap-4">
                          <div className="flex items-center gap-4 min-w-0 flex-1">
                            <div className="flex items-center justify-center w-12 h-12 shrink-0 rounded border border-gray-200 dark:border-[#444] bg-gray-50 dark:bg-[#1A1A1A] text-[#EF4444] dark:text-[#F97316]">
                              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                                <line x1="12" y1="9" x2="12" y2="13" />
                                <line x1="12" y1="17" x2="12.01" y2="17" />
                              </svg>
                            </div>
                            <div className="flex flex-col min-w-0">
                              <h4 className="font-medium text-[15px] md:text-[16px] text-black dark:text-white truncate">
                                {r.originalName}
                              </h4>
                              <p className="text-gray-500 dark:text-gray-400 text-[12px] md:text-[13px] mt-1 leading-relaxed">
                                {/^(network|connection|timeout|gateway|504)/i.test(String(r.error || "").trim())
                                  ? (r.error || "The network connection was lost.")
                                  : VALIDATION_FAILED_RECOVERY_MESSAGE}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Processing / Success Section */}
                {successItems.length > 0 && (
                  <div className="mb-8">
                    <div className="flex items-center justify-between pb-3 border-b border-[#EAEAEA] dark:border-[#2A2A2A] mb-5">
                      <div className="flex items-center gap-2 text-black dark:text-white">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21.5 2v6h-6M2.13 15.57a10 10 0 1 0 3.43-11.44l-5.74 3.44" />
                        </svg>
                        <h2 className="font-medium text-[16px] leading-none">Processing in background</h2>
                      </div>
                      <span className="text-gray-500 dark:text-gray-400 text-[13px] font-medium">
                        {successItems.length}
                      </span>
                    </div>

                    <div className="space-y-4">
                      {successItems.map((r, i) => (
                        <div key={`succ-${i}`} className="w-full rounded-[6px] border border-gray-200 dark:border-[#333] bg-white dark:bg-[#121212] p-[16px] md:p-[20px] flex sm:flex-row flex-col sm:items-center justify-between gap-4">
                          <div className="flex items-center gap-4 min-w-0">
                            <div className="flex items-center justify-center w-12 h-12 shrink-0 rounded border border-gray-200 dark:border-[#444] bg-gray-50 dark:bg-[#1E1E1E] text-gray-400 dark:text-gray-500">
                              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="2" y="2" width="20" height="20" rx="2.18" ry="2.18"></rect>
                                <line x1="7" y1="2" x2="7" y2="22"></line>
                                <line x1="17" y1="2" x2="17" y2="22"></line>
                                <line x1="2" y1="12" x2="22" y2="12"></line>
                                <line x1="2" y1="7" x2="7" y2="7"></line>
                                <line x1="2" y1="17" x2="7" y2="17"></line>
                                <line x1="17" y1="17" x2="22" y2="17"></line>
                                <line x1="17" y1="7" x2="22" y2="7"></line>
                              </svg>
                            </div>
                            <div className="flex flex-col min-w-0">
                              <h4 className="font-medium text-[15px] md:text-[16px] text-black dark:text-white truncate">
                                {r.originalName}
                              </h4>
                              <p className="text-[#999999] text-[12px] mt-0.5 tracking-wide font-mono uppercase">
                                ID: {r.ownerKey || r.mediaId || "Generating..."}
                              </p>
                            </div>
                          </div>

                          <div className="flex items-center gap-2 pl-[64px] sm:pl-0 text-gray-500 dark:text-gray-400 text-[13px] font-medium whitespace-nowrap">
                            <span className="w-1.5 h-1.5 rounded-full bg-black dark:bg-white animate-pulse"></span>
                            Validating...
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex sm:flex-row flex-col gap-4 mt-8 w-full justify-center">
                  <button
                    className="w-full sm:w-[260px] bg-[#0A0A0A] dark:bg-white hover:bg-black dark:hover:bg-gray-200 text-white dark:text-[#0A0A0A] font-medium text-[16px] py-[14px] rounded-lg transition-colors"
                    onClick={goToLibrary}
                  >
                    Go to Library
                  </button>
                  <button
                    className="w-full sm:w-[260px] bg-white dark:bg-transparent border border-[#CFC4C5] dark:border-[#444444] text-[#1B1B1B] dark:text-white hover:bg-[#F9F9F9] dark:hover:bg-[#1E1E1E] font-medium text-[16px] py-[14px] rounded-lg transition-colors"
                    onClick={resetUpload}
                  >
                    Upload Another
                  </button>
                </div>
              </div>
            </div>
          );
        })()}
        {showAudioSetup && !isUploading && uploadResults.length === 0 && (
          <AudioSetupView />
        )}

        {/* Protection Setup View for Image and Video */}
        {!isUploading && uploadResults.length === 0 && !showAudioSetup && showProtectionSetup && (
          <div className="w-full max-w-6xl mx-auto flex flex-col animate-in fade-in zoom-in duration-300 pb-10 text-black dark:text-white font-['Product_Sans']">
            {(() => {
              const currentIndex = selectedFiles.findIndex(f => f.id === activeFileId);
              const activeFile = selectedFiles[currentIndex];
              const isVideo = activeFile?.mediaType === 'video';
              const srcBytes = activeFile?.file?.size || 0;

              return (
                <>
                  {/* Header & Back Button */}
                  <div className="mb-8 mt-4">
                    <button 
                      onClick={() => setShowProtectionSetup(false)} 
                      className="flex items-center gap-2 px-4 py-2 border border-black dark:border-[#2c2c2c] rounded-lg bg-transparent hover:bg-gray-100 dark:hover:bg-[#222] transition-colors mb-8 text-sm text-gray-700 dark:text-gray-300"
                    >
                      ← Back to Selection
                    </button>
                    <h1 className="text-[32px] font-bold mb-1 text-black dark:text-white">Configure Protection</h1>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Set up watermark and NFT registration details.</p>
                  </div>

                  {/* Main Two-Column Layout */}
                  <div className="grid grid-cols-1 lg:grid-cols-[446px_1fr] gap-6 items-start">
                    
                    {/* LEFT COLUMN */}
                    <div className="flex flex-col gap-6">
                      
                      {/* Selected File Navigator */}
                      <div className="w-full p-6 bg-white dark:bg-[#121212] border border-black dark:border-[#2c2c2c] rounded-lg shadow-sm">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-[11px] uppercase tracking-widest text-gray-600 dark:text-gray-400 font-semibold">
                            Selected File ({currentIndex + 1} of {selectedFiles.length})
                          </h3>
                          <div className="flex gap-2">
                            <button
                              onClick={() => {
                                if (currentIndex > 0) {
                                  setActiveFileId(selectedFiles[currentIndex - 1].id);
                                  setTimeout(() => triggerRedraw(), 100);
                                }
                              }}
                              disabled={currentIndex <= 0}
                              className="w-8 h-8 flex items-center justify-center border border-black dark:border-[#2c2c2c] text-gray-600 dark:text-gray-400 bg-transparent hover:bg-gray-100 dark:hover:bg-[#222] disabled:opacity-30 disabled:hover:bg-transparent rounded transition-colors cursor-pointer"
                            >
                              ‹
                            </button>
                            <button
                              onClick={() => {
                                if (currentIndex < selectedFiles.length - 1) {
                                  setActiveFileId(selectedFiles[currentIndex + 1].id);
                                  setTimeout(() => triggerRedraw(), 100);
                                }
                              }}
                              disabled={currentIndex >= selectedFiles.length - 1}
                              className="w-8 h-8 flex items-center justify-center border border-black dark:border-[#2c2c2c] text-gray-600 dark:text-gray-400 bg-transparent hover:bg-gray-100 dark:hover:bg-[#222] disabled:opacity-30 disabled:hover:bg-transparent rounded transition-colors cursor-pointer"
                            >
                              ›
                            </button>
                          </div>
                        </div>
                        <div className="w-full p-3 bg-gray-50 dark:bg-[#0a0a0a] border border-black dark:border-[#2c2c2c] rounded flex items-center gap-3 text-sm">
                          {isVideo ? (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-black dark:text-white opacity-70">
                              <polygon points="23 7 16 12 23 17 23 7"></polygon>
                              <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
                            </svg>
                          ) : (
                            <img 
                              src={require('../assets/images/imageicon.svg').default || require('../assets/images/imageicon.svg')} 
                              alt="Image File" 
                              className="w-4 h-4 opacity-70"
                            />
                          )}
                          <span className="truncate text-gray-800 dark:text-gray-200">{activeFile?.file.name}</span>
                        </div>
                      </div>

                      {/* Protection Method */}
                      <div className="w-full p-6 bg-white dark:bg-[#121212] border border-black dark:border-[#2c2c2c] rounded-lg shadow-sm">
                        <h3 className="text-[16px] font-bold text-black dark:text-white mb-4">Protection Method</h3>
            <div className="grid grid-cols-2 gap-4">
                          <button
                            onClick={() => { setProtectionType('qr'); triggerRedraw(); }}
                            className={`flex flex-col items-center justify-center p-4 rounded-lg border transition-all duration-300 ${
                              protectionType === 'qr'
                                ? 'border-black dark:border-white bg-gray-100 dark:bg-[#1a1a1a]'
                                : 'border-black dark:border-[#2c2c2c] bg-transparent hover:border-gray-500'
                            }`}
                          >
                            <img 
                              src={require('../assets/images/qrimage.svg').default || require('../assets/images/qrimage.svg')} 
                              alt="QR Code" 
                              className="w-8 h-8 mb-2 brightness-0 dark:invert"
                            />
                            <span className="block font-bold text-black dark:text-white text-[13px] mb-1">QR Code</span>
                            <span className="block text-[10px] text-gray-600 dark:text-gray-500">Clean VASTAVIK verification</span>
                          </button>

                          <button
                            onClick={() => { setProtectionType('owner_key'); triggerRedraw(); }}
                            className={`flex flex-col items-center justify-center p-4 rounded-lg border transition-all duration-300 ${
                              protectionType === 'owner_key'
                                ? 'border-black dark:border-white bg-gray-100 dark:bg-[#1a1a1a]'
                                : 'border-black dark:border-[#2c2c2c] bg-transparent hover:border-gray-500'
                            }`}
                          >
                            <img 
                              src={require('../assets/images/vastavikidicon.svg').default || require('../assets/images/vastavikidicon.svg')} 
                              alt="Vastavik ID" 
                              className="w-8 h-8 mb-2 brightness-0 dark:invert"
                            />
                            <span className="block font-bold text-black dark:text-white text-[13px] mb-1">Vastavik ID</span>
                            <span className="block text-[10px] text-gray-600 dark:text-gray-500">Text watermark with shadow</span>
                          </button>
                        </div>
                      </div>

                      {/* Video Quality (Only shown for Videos) */}
                      {isVideo && (
                        <div className="w-full p-6 bg-white dark:bg-[#121212] border border-black dark:border-[#2c2c2c] rounded-lg shadow-sm">
                          <h3 className="text-[16px] font-bold text-black dark:text-white mb-4">Video Quality</h3>
                          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                            {VIDEO_QUALITY_OPTIONS.map((opt) => {
                              const est = estimateVideoSize(srcBytes, opt.value);
                              const selected = videoQuality === opt.value;
                              return (
                                <button
                                  key={opt.value}
                                  type="button"
                                  onClick={() => setVideoQuality(opt.value)}
                                  className={`flex flex-col items-start p-3 rounded-lg border transition-all duration-200 ${
                                    selected ? 'border-black dark:border-white bg-gray-100 dark:bg-[#1a1a1a]' : 'border-black dark:border-[#2c2c2c] bg-transparent hover:border-gray-500'
                                  }`}
                                >
                                  <div className="flex justify-between w-full items-center mb-2">
                                    <span className="text-[13px] font-bold text-black dark:text-white">{opt.label}</span>
                                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${selected ? 'border-black dark:border-white' : 'border-gray-400 dark:border-gray-500'}`}>
                                      {selected && <div className="w-2 h-2 bg-black dark:bg-white rounded-full"></div>}
                                    </div>
                                  </div>
                                  <span className="text-[12px] text-gray-700 dark:text-gray-300 font-medium">
                                    {opt.value === 'original' ? formatSize(est) : `~${formatSize(est)}`}
                                  </span>
                                  <span className="text-[10px] text-gray-600 dark:text-gray-500 mt-1 leading-tight">{opt.blurb}</span>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* NFT Registration Type */}
                      <div className="w-full p-6 bg-white dark:bg-[#121212] border border-black dark:border-[#2c2c2c] rounded-lg shadow-sm">
                        <h3 className="text-[16px] font-bold text-black dark:text-white mb-4">NFT Registration</h3>
                        <div className="space-y-3">
                          <button
                            onClick={() => setNftType('private')}
                            className={`w-full flex items-start gap-4 p-4 rounded-lg border transition-all duration-300 text-left ${
                              nftType === 'private'
                                ? 'border-black dark:border-white bg-gray-100 dark:bg-[#1a1a1a]'
                                : 'border-black dark:border-[#2c2c2c] bg-transparent hover:border-gray-500 dark:hover:border-[#333]'
                            }`}
                          >
                            <div className={`mt-0.5 w-4 h-4 rounded-full border flex items-center justify-center ${nftType === 'private' ? 'border-black dark:border-white' : 'border-gray-400 dark:border-[#555]'}`}>
                              {nftType === 'private' && <div className="w-2 h-2 rounded-full bg-black dark:bg-white"></div>}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-bold text-black dark:text-white text-[14px]">Private NFT</span>
                                <span className="bg-gray-200 dark:bg-[#2c2c2c] text-gray-700 dark:text-gray-300 text-[10px] px-2 py-0.5 rounded-sm">Default</span>
                              </div>
                              <span className="block text-xs text-gray-600 dark:text-gray-500">Only you and approved users can view.</span>
                            </div>
                          </button>

                          <button
                            disabled
                            className="w-full flex items-start gap-4 p-4 rounded-lg border border-black dark:border-[#2c2c2c] bg-transparent opacity-60 cursor-not-allowed text-left"
                          >
                            <div className="mt-0.5 w-4 h-4 rounded-full border border-gray-400 dark:border-[#555] flex items-center justify-center"></div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-bold text-gray-700 dark:text-gray-300 text-[14px]">Public NFT</span>
                                <span className="bg-gray-200 dark:bg-[#2c2c2c] text-gray-600 dark:text-gray-400 text-[10px] px-2 py-0.5 rounded-sm">Coming Soon</span>
                              </div>
                              <span className="block text-xs text-gray-600 dark:text-gray-500">Visible to everyone on the blockchain.</span>
                            </div>
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* RIGHT COLUMN */}
                    <div className="flex flex-col gap-6 w-full min-w-0">
                      
                      {/* Preview Container */}
                      <div className="w-full relative rounded-lg overflow-hidden border border-black dark:border-[#2c2c2c] bg-white dark:bg-[#121212] min-h-[300px] flex items-center justify-center shadow-sm group">
                        {previewImage || (previewVideo && videoPreviewReady) ? (
                          <>
                            <canvas
                              ref={previewCanvasRef}
                              style={{ width: '100%', height: 'auto', maxHeight: '500px', objectFit: 'contain' }}
                            />
                            {/* Hover Overlay Button to show full preview */}
                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                               <button 
                                 onClick={() => setShowPreview(true)}
                                 className="flex items-center gap-2 px-5 py-2.5 bg-white/80 dark:bg-[#222]/80 backdrop-blur border border-black dark:border-[#333] rounded-full text-sm font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#333] transition-colors"
                               >
                                 <span>👁️</span> Show Full Preview
                               </button>
                            </div>

                            {/* PLAYABLE VIDEO MODAL OR FULL IMAGE PREVIEW */}
                            {showPreview && (
                              <div 
                                className="fixed inset-0 bg-black/95 z-[9999] flex flex-col items-center justify-center p-4 md:p-8"
                                onClick={() => setShowPreview(false)}
                              >
                                <div className="relative flex flex-col items-center justify-center max-w-full max-h-full" onClick={e => e.stopPropagation()}>
                                  <button 
                                    onClick={() => setShowPreview(false)} 
                                    className="absolute -top-14 right-0 md:-right-14 md:top-0 w-10 h-10 bg-[#2c2c2c] hover:bg-[#333] border border-[#444] text-white rounded-full text-xl flex items-center justify-center shadow-lg transition-transform hover:scale-105 z-[10000]"
                                  >
                                    ×
                                  </button>
                                  
                                  {isVideo && activeFile?.preview ? (
                                    <div 
                                      className="relative flex max-w-full max-h-[85vh] rounded-lg shadow-[0_20px_60px_rgba(0,0,0,0.5)] overflow-hidden bg-black"
                                      style={{ aspectRatio: `${activeFile.dimensions?.width || 16} / ${activeFile.dimensions?.height || 9}` }}
                                    >
                                      <video 
                                        src={activeFile.preview} 
                                        controls 
                                        autoPlay 
                                        className="w-full h-full object-contain" 
                                      />
                                      {/* Exact scalable transparent canvas overlay for video playing */}
                                      <canvas 
                                        ref={modalOverlayCanvasRef}
                                        className="absolute inset-0 w-full h-full pointer-events-none"
                                      />
                                    </div>
                                  ) : (
                                    <canvas ref={modalCanvasRef} className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-[0_20px_60px_rgba(0,0,0,0.5)]" />
                                  )}
                                </div>
                              </div>
                            )}
                          </>
                        ) : (
                          <div className="flex flex-col items-center text-gray-500">
                            <img 
                              src={require('../assets/images/imageicon.svg').default || require('../assets/images/imageicon.svg')} 
                              alt="Preview Placeholder" 
                              className="w-12 h-12 mb-3 opacity-30" 
                            />
                            <p className="text-sm">Preview generating...</p>
                          </div>
                        )}
                      </div>

                      {/* Watermark Settings */}
                      <div className="w-full bg-white dark:bg-[#121212] border border-black dark:border-[#2c2c2c] rounded-lg p-6 shadow-sm">
                        <h3 className="text-[11px] uppercase tracking-widest text-black dark:text-white mb-6 font-semibold">Watermark Settings</h3>
                        
                        {/* Position Selection */}
                        <div className="mb-8">
                          <div className="text-sm text-gray-600 dark:text-gray-400 mb-3">Position</div>
                          <div className="flex flex-wrap gap-3">
                            {POSITIONS.filter(p => p.id !== 'center').map(pos => {
                              const isSelected = (protectionType === 'qr' ? qrPosition : ownerKeyPosition) === pos.id;
                              
                              const getPosIconSrc = (id: string) => {
                                  try {
                                      let imgPath;
                                      switch(id) {
                                        case 'top-left':
                                          imgPath = require('../assets/images/leftqr.svg');
                                          break;
                                        case 'top-right':
                                          imgPath = require('../assets/images/rightqr.svg');
                                          break;
                                        case 'bottom-left':
                                          imgPath = require('../assets/images/bottomleftqr.svg');
                                          break;
                                        case 'bottom-right':
                                          imgPath = require('../assets/images/bottomrightqr.svg');
                                          break;
                                        case 'center':
                                          imgPath = require('../assets/images/centerqr.svg');
                                          break;
                                        default:
                                          return null;
                                      }
                                      return imgPath.default || imgPath;
                                  } catch (error) {
                                      return null; 
                                  }
                              };
                              
                              const iconSrc = getPosIconSrc(pos.id);

                              return (
                         <button
                                  key={pos.id}
                                  onClick={() => {
                                    if (protectionType === 'qr') setQrPosition(pos.id);
                                    else setOwnerKeyPosition(pos.id);
                                    triggerRedraw();
                                  }}
                                  className={`w-[61px] h-[61px] flex items-center justify-center rounded border transition-all duration-200 ${
                                    isSelected 
                                      ? 'border-black dark:border-white bg-gray-100 dark:bg-[#1F1F1F]' 
                                      : 'border-black dark:border-[#2c2c2c] bg-transparent hover:border-gray-500 dark:hover:border-[#555]'
                                  }`}
                                  title={pos.label}
                                >
                                  {iconSrc ? (
                                      <img 
                                        src={iconSrc} 
                                        alt={pos.label} 
                                        className="w-7 h-7 brightness-0 dark:invert" 
                                      />
                                  ) : (
                                      <span className="text-2xl">{pos.icon}</span>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        {/* Size Slider */}
                        <div>
                          <div className="flex justify-between items-center mb-3">
                            <span className="text-sm text-gray-600 dark:text-gray-400">Size</span>
                            <span className="text-sm text-black dark:text-white">
                              {(protectionType === 'qr' ? qrSize : ownerKeyFontSize) === 0 
                                ? `Auto (${protectionType === 'qr' ? calculatedQrSize : calculatedFontSize}px)` 
                                : `${protectionType === 'qr' ? qrSize : ownerKeyFontSize}px`}
                            </span>
                          </div>
                        <input
                            type="range"
                            min={protectionType === 'qr' ? Math.round(calculatedQrSize * 0.5) : 8}
                            max={protectionType === 'qr' ? Math.round(calculatedQrSize * 1.5) : 32}
                            step={protectionType === 'qr' ? "5" : "2"}
                            value={(protectionType === 'qr' ? qrSize : ownerKeyFontSize) === 0 
                              ? (protectionType === 'qr' ? calculatedQrSize : calculatedFontSize) 
                              : (protectionType === 'qr' ? qrSize : ownerKeyFontSize)}
                            onChange={(e) => {
                              const val = Number(e.target.value);
                              if (protectionType === 'qr') {
                                setQrSize(Math.abs(val - calculatedQrSize) <= 5 ? 0 : val);
                              } else {
                                setOwnerKeyFontSize(Math.abs(val - calculatedFontSize) <= 2 ? 0 : val);
                              }
                              triggerRedraw();
                            }}
                            className="w-full h-1 bg-black dark:bg-white rounded-lg appearance-none cursor-pointer accent-black dark:accent-white"
                          />
                          <div className="flex justify-between text-[11px] text-gray-600 dark:text-gray-500 mt-2">
                            <span>Min ({(protectionType === 'qr' ? Math.round(calculatedQrSize * 0.5) : 8)}px)</span>
                            <span>Max ({(protectionType === 'qr' ? Math.round(calculatedQrSize * 1.5) : 32)}px)</span>
                          </div>
                        </div>
                      </div>

                      {/* Protection Summary */}
                      <div className="w-full bg-white dark:bg-[#121212] border border-black dark:border-[#2c2c2c] rounded-lg p-6 shadow-sm">
                        <h3 className="text-[11px] uppercase tracking-widest text-black dark:text-white mb-4 font-semibold">
                          Protection Summary
                        </h3>
                        <div className="flex flex-wrap gap-3">
                          
                          {/* Summary Block 1 */}
                          <div className="flex items-center gap-2 px-3 py-2 border border-black dark:border-[#2c2c2c] rounded-md bg-gray-50 dark:bg-[#0a0a0a] text-[13px] text-gray-800 dark:text-gray-200 font-medium">
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="opacity-90">
                              <rect x="3" y="3" width="7" height="7"></rect>
                              <rect x="14" y="3" width="7" height="7"></rect>
                              <rect x="14" y="14" width="7" height="7"></rect>
                              <rect x="3" y="14" width="7" height="7"></rect>
                            </svg>
                            {protectionType === 'qr' ? 'VASTAVIK QR Code' : 'Vastavik ID Watermark'}
                          </div>

                          {/* Summary Block 2 */}
                          <div className="flex items-center gap-2 px-3 py-2 border border-black dark:border-[#2c2c2c] rounded-md bg-gray-50 dark:bg-[#0a0a0a] text-[13px] text-gray-800 dark:text-gray-200 font-medium">
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="opacity-90">
                              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                              <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                            </svg>
                            Private NFT
                          </div>

                          {/* Summary Block 3 */}
                          <div className="flex items-center gap-2 px-3 py-2 border border-black dark:border-[#2c2c2c] rounded-md bg-gray-50 dark:bg-[#0a0a0a] text-[13px] text-gray-800 dark:text-gray-200 font-medium">
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="opacity-90">
                              <path d="M12 2l3.09 1.26 3.1-.11 1.25 3.1.11 3.1L22 12l-1.26 3.09.11 3.1-3.1 1.25-.11 3.1L12 22l-3.09-1.26-3.1.11-1.25-3.1-.11-3.1L2 12l1.26-3.09-.11-3.1 3.1-1.25.11-3.1z"></path>
                              <polyline points="9 12 11 14 15 10"></polyline>
                            </svg>
                            Blockchain Verified
                          </div>

                        </div>
                      </div>

                    </div>
                  </div>

                  {/* Upload Button */}
                  <div className="w-full flex justify-end mt-8">
                    <button 
                      onClick={() => performUpload({ type: protectionType, qrPosition, qrSize, qrErrorCorrection, ownerKeyPosition, ownerKeyColor, ownerKeyFontSize, nftType })} 
                      className="px-8 py-3 rounded-md bg-black text-white dark:bg-white dark:text-black font-bold text-sm shadow-lg hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors"
                    >
                      Upload & Protect
                    </button>
                  </div>
                </>
              );
            })()}
          </div>
        )}

        {/* ✅ INITIAL DRAG & DROP STATE */}
        {!isUploading && uploadResults.length === 0 && !showProtectionSetup && !showAudioSetup && (
          <div className="w-full flex flex-col bg-transparent">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*,video/*,audio/*,.mp3,.wav,.m4a,.aac,.ogg,.flac"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />

            {/* HEADER (Moved completely ABOVE the two columns) */}
            <div className="mb-6 text-left w-full max-w-[686px]">
              <h1 className="font-['Caviar_Dreams'] font-bold text-[28px] md:text-[32px] text-black dark:text-white mb-2 leading-tight">
                Upload & Validate Media
              </h1>
              <p className="font-['Product_Sans'] font-light text-[13px] md:text-[14px] leading-[20px] text-gray-600 dark:text-[#A0A0A0]">
                To successfully validate a media make sure that it's not shared media (e.g. WhatsApp), downloaded or copied from the internet. Or If you have edited the media we may ask you for the original for validation.
              </p>
            </div>

            {/* TWO COLUMNS: Upload Area (Left) | Process Flow (Right) */}
            <div className="flex flex-col lg:flex-row gap-10 lg:gap-14 w-full items-start">
              
              {/* LEFT COLUMN: Upload Area */}
              <div className="flex-1 flex flex-col w-full max-w-[720px]">
                {selectedFiles.length === 0 ? (
                  <>
               {/* ✅ DOUBLE BORDER DRAG & DROP BOX */}
                    <div className="w-full p-2.5 rounded-xl border border-gray-300 dark:border-[#2C2C2C] bg-gray-50 dark:bg-[#121212] mb-4">
                      <div
                        className={`relative w-full flex flex-col items-center justify-center py-10 px-4 rounded-lg border-2 border-dashed transition-all duration-200 ${
                          isDragging ? 'border-blue-500 bg-blue-100 dark:bg-blue-900/10 scale-[0.99]' : 'border-black dark:border-white bg-transparent'
                        }`}
                        onDrop={handleDrop}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                      >
                        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-black dark:text-white mb-3">
                          <path d="M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9Z"></path>
                          <polyline points="12 12 12 15"></polyline>
                          <polyline points="9 12 12 9 15 12"></polyline>
                        </svg>

                        <h2 className="font-['Caviar_Dreams'] font-bold text-[24px] md:text-[26px] text-black dark:text-white mb-1 text-center">
                          Drag & Drop Media
                        </h2>
                        <p className="font-['Product_Sans'] font-light text-[13px] text-gray-500 dark:text-[#A0A0A0] mb-6 text-center">
                          or select a specific category below
                        </p>

                        <div className="flex flex-col sm:flex-row gap-4 z-10">
                          {/* Photos & Videos Box */}
                          <div
                            className="flex flex-col items-center justify-between w-[140px] h-[130px] p-4 rounded-lg bg-white dark:bg-[#1E1E1E] hover:bg-gray-100 dark:hover:bg-[#252525] transition-colors cursor-pointer shadow-sm dark:shadow-none border border-gray-200 dark:border-[#2C2C2C]"
                            onClick={() => {
                              if (fileInputRef.current) {
                                fileInputRef.current.accept = "image/*,video/*";
                                fileInputRef.current.click();
                                setTimeout(() => {
                                  if (fileInputRef.current) fileInputRef.current.accept = "image/*,video/*,audio/*,.mp3,.wav,.m4a,.aac,.ogg,.flac";
                                }, 100);
                              }
                            }}
                          >
                            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-black dark:text-white mt-1">
                              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                              <circle cx="8.5" cy="8.5" r="1.5"></circle>
                              <polyline points="21 15 16 10 5 21"></polyline>
                            </svg>
                            <span className="font-['Product_Sans'] text-[12px] text-black dark:text-white text-center w-full mt-1 mb-2">Photos & Videos</span>
                            <button className="w-full py-1.5 bg-black text-white dark:bg-white dark:text-black font-['Product_Sans'] font-bold text-[11px] rounded-md pointer-events-none">
                              Select Media
                            </button>
                          </div>

                          {/* Audio Files Box */}
                          <div
                            className="flex flex-col items-center justify-between w-[140px] h-[130px] p-4 rounded-lg bg-white dark:bg-[#1E1E1E] hover:bg-gray-100 dark:hover:bg-[#252525] transition-colors cursor-pointer shadow-sm dark:shadow-none border border-gray-200 dark:border-[#2C2C2C]"
                            onClick={() => {
                              if (fileInputRef.current) {
                                fileInputRef.current.accept = "audio/*,.mp3,.wav,.m4a,.aac,.ogg,.flac";
                                fileInputRef.current.click();
                                setTimeout(() => {
                                  if (fileInputRef.current) fileInputRef.current.accept = "image/*,video/*,audio/*,.mp3,.wav,.m4a,.aac,.ogg,.flac";
                                }, 100);
                              }
                            }}
                          >
                            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-black dark:text-white mt-1">
                              <path d="M9 18V5l12-2v13"></path>
                              <circle cx="6" cy="18" r="3"></circle>
                              <circle cx="18" cy="16" r="3"></circle>
                            </svg>
                            <span className="font-['Product_Sans'] text-[12px] text-black dark:text-white text-center w-full mt-1 mb-2">Audio Files</span>
                            <button className="w-full py-1.5 bg-black text-white dark:bg-white dark:text-black font-['Product_Sans'] font-bold text-[11px] rounded-md pointer-events-none">
                              Select Audio
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Format Specs Banner */}
                    <div className="w-full flex flex-wrap sm:flex-nowrap items-center justify-center gap-x-2 gap-y-1.5 py-3 px-4 rounded-lg border border-gray-300 dark:border-[#2C2C2C] bg-white sm:bg-gray-50 dark:bg-[#121212]">
                      <div className="flex items-center gap-1 font-['Product_Sans'] text-[9px] tracking-wide uppercase text-gray-500 dark:text-[#A0A0A0]">
                        <span className="w-3 h-3 flex items-center justify-center border border-gray-400 dark:border-[#A0A0A0] rounded-full text-[7px]">i</span>
                        <span className="text-black dark:text-white font-bold ml-0.5">IMAGES:</span> JPEG, PNG, WEBP, GIF, HEIC
                      </div>
                      <div className="hidden sm:block text-gray-300 dark:text-[#2C2C2C] text-[10px]">|</div>
                      <div className="flex items-center font-['Product_Sans'] text-[9px] tracking-wide uppercase text-gray-500 dark:text-[#A0A0A0]">
                        <span className="text-black dark:text-white font-bold mr-0.5">VIDEOS:</span> MP4, WEBM, MOV, AVI
                      </div>
                      <div className="hidden sm:block text-gray-300 dark:text-[#2C2C2C] text-[10px]">|</div>
                      <div className="flex items-center font-['Product_Sans'] text-[9px] tracking-wide uppercase text-gray-500 dark:text-[#A0A0A0]">
                        <span className="text-black dark:text-white font-bold mr-0.5">AUDIO:</span> MP3, WAV, M4A, AAC, FLAC
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="w-full">
                    <div className="flex justify-between items-center mb-3">
                      <span className="text-lg font-semibold font-['Caviar_Dreams'] text-black dark:text-white">{selectedFiles.length} file(s) selected</span>
                      <button className="text-red-500 dark:text-red-400 hover:text-red-600 dark:hover:text-red-300 font-medium font-['Product_Sans'] transition-colors px-2.5 py-1 bg-red-50 dark:bg-red-500/10 rounded-md text-xs" onClick={(e) => { e.stopPropagation(); clearAll(); }}>
                        Clear All
                      </button>
                    </div>
                    
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
                      {selectedFiles.map(file => (
                        <div
                          key={file.id}
                          className={`relative bg-white dark:bg-[#1E1E1E] shadow-sm dark:shadow-none rounded-lg overflow-hidden cursor-pointer transition-colors border-2 ${file.id === activeFileId ? 'border-blue-500' : 'border-gray-200 dark:border-[#2C2C2C] hover:border-gray-300 dark:hover:border-white/20'}`}
                          onClick={() => setActiveFileId(file.id)}
                        >
                          <div className="w-full h-24 bg-gray-100 dark:bg-black/40 flex items-center justify-center relative">
                            {(file.mediaType === 'image' || file.mediaType === 'video') && file.preview ? (
                              file.mediaType === 'video' ? (
                                <video src={`${file.preview}#t=0.1`} className="w-full h-full object-cover" preload="metadata" muted playsInline />
                              ) : (
                                <img src={file.preview} alt={file.file.name} className="w-full h-full object-cover" />
                              )
                            ) : (
                              <div className="text-2xl">
                                {file.mediaType === 'video' ? '🎥' : file.mediaType === 'audio' ? '🎵' : '📄'}
                              </div>
                            )}
                            <button
                              className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-red-500 transition-colors text-[10px] z-10"
                              onClick={(e) => { e.stopPropagation(); removeFile(file.id); }}
                            >
                              ✕
                            </button>
                          </div>
                          <div className="p-2.5">
                            <div className="truncate text-[12px] font-bold font-['Product_Sans'] text-black dark:text-white mb-0.5">{file.file.name}</div>
                            <div className="text-[10px] font-['Product_Sans'] text-gray-500 dark:text-gray-400">{formatFileSize(file.file.size)}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    <button
                      className="w-full py-3 border border-dashed border-gray-300 dark:border-[#2C2C2C] rounded-lg text-gray-600 dark:text-[#A0A0A0] hover:text-black dark:hover:text-white hover:bg-gray-50 dark:hover:bg-[#252525] transition-all mb-4 font-medium text-[13px] font-['Product_Sans']"
                      onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
                    >
                      + Add more files
                    </button>

                    {error && <div className="bg-red-50 dark:bg-red-500/10 border border-red-500 text-red-600 dark:text-red-500 p-2.5 rounded-lg mb-4 text-[13px] font-medium font-['Product_Sans']">{error}</div>}

                    <button
                      className="w-full py-3 rounded-lg bg-blue-600 hover:bg-blue-500 text-white font-bold text-[14px] font-['Product_Sans'] shadow-md shadow-blue-900/20 transition-transform active:scale-[0.98]"
                      onClick={handleProceed}
                    >
                      {hasImages || selectedFiles.some(f => f.mediaType === 'video')
                        ? 'Configure Protection & Upload'
                        : hasAudioFiles
                        ? 'Configure Audio Protection & Upload'
                        : 'Upload Files'}
                    </button>
                  </div>
                )}
              </div>

              {/* RIGHT COLUMN: Process Flow */}
              <div className="w-full lg:w-[280px] shrink-0">
                <div className="border-b border-gray-300 dark:border-[#2C2C2C] mb-5 pb-2">
                  <h3 className="font-['Caviar_Dreams'] text-[18px] md:text-[20px] uppercase tracking-widest text-black dark:text-white">
                    Process Flow
                  </h3>
                </div>

                <div className="space-y-5">
                  {/* Step 1 */}
                  <div className="flex gap-3 items-start">
                    <div className="w-9 h-9 shrink-0 flex items-center justify-center bg-gray-200 dark:bg-[#1E1E1E] text-black dark:text-white font-['Product_Sans'] text-[13px] rounded-sm">01</div>
                    <div className="pt-0.5">
                      <h4 className="font-['Product_Sans'] font-normal text-[14px] leading-tight text-black dark:text-white">Select Media</h4>
                      <p className="font-['Product_Sans'] font-light text-[12px] leading-snug text-gray-500 dark:text-[#A0A0A0] mt-1">Choose photos, videos, or audio files</p>
                    </div>
                  </div>
                  {/* Step 2 */}
                  <div className="flex gap-3 items-start">
                    <div className="w-9 h-9 shrink-0 flex items-center justify-center bg-gray-200 dark:bg-[#1E1E1E] text-black dark:text-white font-['Product_Sans'] text-[13px] rounded-sm">02</div>
                    <div className="pt-0.5">
                      <h4 className="font-['Product_Sans'] font-normal text-[14px] leading-tight text-black dark:text-white">Configure Protection</h4>
                      <p className="font-['Product_Sans'] font-light text-[12px] leading-snug text-gray-500 dark:text-[#A0A0A0] mt-1">Set QR, Vastavik ID, or Vastavik Voice ID</p>
                    </div>
                  </div>
                  {/* Step 3 */}
                  <div className="flex gap-3 items-start">
                    <div className="w-9 h-9 shrink-0 flex items-center justify-center bg-gray-200 dark:bg-[#1E1E1E] text-black dark:text-white font-['Product_Sans'] text-[13px] rounded-sm">03</div>
                    <div className="pt-0.5">
                      <h4 className="font-['Product_Sans'] font-normal text-[14px] leading-tight text-black dark:text-white">Upload & Validate</h4>
                      <p className="font-['Product_Sans'] font-light text-[12px] leading-snug text-gray-500 dark:text-[#A0A0A0] mt-1">AI validates originality and uploads securely</p>
                    </div>
                  </div>
                  {/* Step 4 */}
                  <div className="flex gap-3 items-start">
                    <div className="w-9 h-9 shrink-0 flex items-center justify-center bg-gray-200 dark:bg-[#1E1E1E] text-black dark:text-white font-['Product_Sans'] text-[13px] rounded-sm">04</div>
                    <div className="pt-0.5">
                      <h4 className="font-['Product_Sans'] font-normal text-[14px] leading-tight text-black dark:text-white">Blockchain Registry</h4>
                      <p className="font-['Product_Sans'] font-light text-[12px] leading-snug text-gray-500 dark:text-[#A0A0A0] mt-1">Get permanent authenticity proof</p>
                    </div>
                  </div>
                  {/* Step 5 */}
                  <div className="flex gap-3 items-start">
                    <div className="w-9 h-9 shrink-0 flex items-center justify-center bg-gray-200 dark:bg-[#1E1E1E] text-black dark:text-white font-['Product_Sans'] text-[13px] rounded-sm">05</div>
                    <div className="pt-0.5">
                      <h4 className="font-['Product_Sans'] font-normal text-[14px] leading-tight text-black dark:text-white">Ready to Share & Publish</h4>
                      <p className="font-['Product_Sans'] font-light text-[12px] leading-snug text-gray-500 dark:text-[#A0A0A0] mt-1">Share or Publish directly from the control Panel</p>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        )}
      </div>

    </div>
  );
};

export default UploadDemo;